import * as React from 'react';
import { ComponentPropsWithoutRef, ForwardedRef, forwardRef } from 'react';
import { Input as HeadlessInput, InputProps } from '@headlessui/react';
import {
  CheckIcon,
  cn,
  Spinner,
  TriangleExclamationIcon,
} from '@quickmd-llc/design-system';
import {
  useAdjustRightPaddingBasedOnRightSideContent,
  INPUT_DEFAULT_RIGHT_SIZE_PADDING,
} from './useAdjustRightPaddingBasedOnRightSideContent';

export function InputGroup({ children }: ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot="control"
      className={cn(
        'relative isolate block',
        '[&_input]:has-[[data-slot=rightSideContent]:first-child]:pl-10 [&_input]:has-[[data-slot=rightSideContent]:last-child]:pr-10',
        '[&>[data-slot=rightSideContent]]:pointer-events-none [&>[data-slot=rightSideContent]]:absolute [&>[data-slot=rightSideContent]]:top-4 [&>[data-slot=rightSideContent]]:z-10 [&>[data-slot=rightSideContent]]:size-4 [&>[data-slot=rightSideContent]]:text-shade-70',
        '[&>[data-slot=rightSideContent]:first-child]:left-4 [&>[data-slot=rightSideContent]:last-child]:right-3',
      )}
    >
      {children}
    </span>
  );
}

type InputType =
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'url';

type Props = {
  className?: string;
  error?: boolean;
  loading?: boolean;
  success?: boolean;
  disableNativeNumericControls?: boolean;
  type?: InputType;
  rightSideLabel?: React.ReactNode;
} & Omit<InputProps, 'as' | 'className'>;

/**
 * @deprecated
 * This Input component is deprecated.
 * Please use the new `Input` component from `@quickmd-llc/design-system/forms` instead.
 *
 * This version will be removed in a future release.
 */
export const Input = forwardRef(function Input(
  {
    className,
    error = false,
    loading = false,
    success = false,
    disableNativeNumericControls,
    rightSideLabel = null,
    ...props
  }: Props,
  forwardedRef: ForwardedRef<HTMLInputElement>,
) {
  const showErrorIcon = error && !loading;
  const showSuccessIcon = success && !loading;

  // !!This setup is temporary. We need this, but it has yet to be designed to take the error/loading/success
  // into account. When designs are ready, we will need to revise. however the logic is such that we should be
  // able to drop any content on right hand side, and the input will work correctly
  const { ref, rightSideContentRef } =
    useAdjustRightPaddingBasedOnRightSideContent(forwardedRef);
  const hasRightSideContent =
    Boolean(rightSideLabel) || loading || showErrorIcon || showSuccessIcon;

  return (
    <span
      data-slot="control"
      className={cn([
        className,
        // Basic layout
        'relative block w-full',
        // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
        'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
        // Focus ring
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent',
        // Disabled state
        'before:has-[[data-disabled]]:shadow-none',
        // Right side content
        'z-10 [&>[data-slot=rightSideContent]]:pointer-events-none [&>[data-slot=rightSideContent]]:absolute [&>[data-slot=rightSideContent]]:top-1/2 [&>[data-slot=rightSideContent]]:-translate-y-1/2',
        '[&>[data-slot=rightSideContent]:first-child]:left-3 [&>[data-slot=rightSideContent]:last-child]:right-4',
      ])}
    >
      <HeadlessInput
        data-slot="input"
        ref={ref}
        {...props}
        className={cn(
          // Basic layout
          [
            'relative block h-11 w-full appearance-none rounded-lg border py-3',
            INPUT_DEFAULT_RIGHT_SIZE_PADDING,
          ],
          // Typography
          'text-base text-text-dark placeholder:text-text-light',
          // Idle
          'border-border-dark',
          // Hover
          'data-[hover]:border-border-darker data-[hover]:shadow-sm',
          // Active
          'data-[focus]:border-border-focus data-[focus]:shadow-sm data-[focus]:outline-2',
          // Border (Outline)
          'outline outline-1 outline-border-focus',
          // Invalid state
          'data-[invalid="true"]:border data-[invalid="true"]:border-error data-[invalid="true"]:bg-surface-error data-[invalid="true"]:text-text-error',
          //Invalid State Hover
          'data-[invalid="true"]:data-[hover]:border',
          // Disabled state
          'data-[disabled]:cursor-not-allowed data-[disabled]:bg-surface-disabled',
          disableNativeNumericControls &&
            props.type === 'number' &&
            '[&::-webkit-inner-spin-button]:appearance-none',
          className,
        )}
      />

      {hasRightSideContent && (
        <span ref={rightSideContentRef} data-slot="rightSideContent">
          {loading ? (
            <Spinner iconSize="l" className="animate-spin text-shade-70" />
          ) : showErrorIcon ? (
            <TriangleExclamationIcon iconSize="l" className="text-text-error" />
          ) : showSuccessIcon ? (
            <CheckIcon iconSize="l" className="text-seafoam-50" />
          ) : (
            rightSideLabel
          )}
        </span>
      )}
    </span>
  );
});
